import type { ContentSettingsKind, SettingsTarget } from './ScopedSettings'

export const scopedSettingsReturnKey = 'snowvault.scoped-settings-return.v1'

export type ScopedSettingsReturn = {
  returnPath: string
  target: SettingsTarget
}

function isContentKind(value: unknown): value is ContentSettingsKind | undefined {
  return value === undefined || value === 'files' || value === 'text' || value === 'connectors'
}

function isContentItemId(value: unknown): value is number | undefined {
  return value === undefined || (typeof value === 'number' && Number.isSafeInteger(value) && value > 0)
}

function isSettingsTarget(value: unknown): value is SettingsTarget {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  if (!isContentKind(candidate['contentKind']) || !isContentItemId(candidate['contentItemId'])) return false
  if (candidate['scope'] === 'profile') {
    return candidate['section'] === 'profile' || candidate['section'] === 'security'
  }
  if (candidate['scope'] === 'organisation') {
    return candidate['section'] === 'account' || candidate['section'] === 'users' || candidate['section'] === 'content'
  }
  return (
    candidate['scope'] === 'workspace' &&
    typeof candidate['projectId'] === 'number' &&
    Number.isSafeInteger(candidate['projectId']) &&
    candidate['projectId'] > 0 &&
    (candidate['section'] === 'general' || candidate['section'] === 'content' || candidate['section'] === 'danger')
  )
}

export function rememberScopedSettingsReturn(target: SettingsTarget): void {
  try {
    const returnPath = `${window.location.pathname}${window.location.search}${window.location.hash}`
    if (!returnPath.startsWith('/')) return
    const value: ScopedSettingsReturn = { returnPath, target }
    window.sessionStorage.setItem(scopedSettingsReturnKey, JSON.stringify(value))
  } catch {
    // Storage can be unavailable in privacy-restricted browsers. OAuth itself can
    // continue through the legacy callback route even when the dialog cannot reopen.
  }
}

export function takeScopedSettingsReturn(): ScopedSettingsReturn | undefined {
  try {
    const raw = window.sessionStorage.getItem(scopedSettingsReturnKey)
    window.sessionStorage.removeItem(scopedSettingsReturnKey)
    if (raw === null) return undefined
    const value: unknown = JSON.parse(raw)
    if (typeof value !== 'object' || value === null) return undefined
    const candidate = value as Record<string, unknown>
    if (
      typeof candidate['returnPath'] !== 'string' ||
      !candidate['returnPath'].startsWith('/') ||
      candidate['returnPath'].startsWith('//') ||
      !isSettingsTarget(candidate['target'])
    ) {
      return undefined
    }
    return { returnPath: candidate['returnPath'], target: candidate['target'] }
  } catch {
    return undefined
  }
}
